import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { inputCls, Btn } from "@/admin/ui";

export default function AdminLogin() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState("password"); // password | setup | verify | backup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [otpauth, setOtpauth] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [backupInput, setBackupInput] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [busy, setBusy] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/api/auth/login", { email, password });
      if (data.mfa_setup_required) {
        setSetupToken(data.setup_token);
        const s = await api.post("/api/auth/mfa/setup", { setup_token: data.setup_token });
        setSecret(s.data.secret);
        setOtpauth(s.data.otpauth_uri);
        setStep("setup");
      } else if (data.mfa_required) {
        setMfaToken(data.mfa_token);
        setStep("verify");
      }
    } catch (err) {
      toast.error(apiError(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  const submitEnable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/api/auth/mfa/enable", { setup_token: setupToken, code });
      setBackupCodes(data.backup_codes);
      setCode("");
      setStep("backup");
    } catch (err) {
      toast.error(apiError(err, "Invalid code"));
    } finally {
      setBusy(false);
    }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/api/auth/mfa/verify", { mfa_token: mfaToken, code, backup_code: backupInput });
      await refresh();
      nav("/admin");
    } catch (err) {
      toast.error(apiError(err, "Invalid code"));
    } finally {
      setBusy(false);
    }
  };

  const finishBackup = async () => {
    await refresh();
    nav("/admin");
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-azeka-navy px-5 py-12">
      <div className="grain absolute inset-0 opacity-30" />
      <div className="relative w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl" data-testid="admin-login">
        <div className="mb-8 flex justify-center"><Logo className="h-11 w-auto" /></div>

        {step === "password" && (
          <form onSubmit={submitPassword} className="space-y-4">
            <h1 className="text-center text-xl font-semibold text-ink-heading">Admin sign in</h1>
            <input data-testid="login-email" type="email" placeholder="Email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} required />
            <input data-testid="login-password" type="password" placeholder="Password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} required />
            <Btn data-testid="login-submit" type="submit" disabled={busy} className="w-full">{busy ? "Signing in…" : "Continue"}</Btn>
            <button type="button" onClick={() => nav("/admin/forgot")} className="block w-full text-center text-xs text-ink-muted hover:text-azeka-teal">Forgot password?</button>
          </form>
        )}

        {step === "setup" && (
          <form onSubmit={submitEnable} className="space-y-4" data-testid="mfa-setup">
            <h1 className="text-center text-xl font-semibold text-ink-heading">Set up two-factor authentication</h1>
            <p className="text-center text-sm text-ink-muted">Scan this QR code with an authenticator app, then enter the 6-digit code.</p>
            <div className="flex justify-center rounded-xl bg-white p-4">
              {otpauth ? <QRCodeSVG value={otpauth} size={168} /> : null}
            </div>
            <p className="break-all rounded-lg bg-slate-100 p-3 text-center text-xs text-ink-muted">Manual key: <span className="font-mono font-semibold text-ink-body">{secret}</span></p>
            <input data-testid="mfa-setup-code" inputMode="numeric" placeholder="123456" className={`${inputCls} text-center tracking-widest`} value={code} onChange={(e) => setCode(e.target.value)} required />
            <Btn data-testid="mfa-enable-submit" type="submit" variant="teal" disabled={busy} className="w-full">{busy ? "Verifying…" : "Enable & continue"}</Btn>
          </form>
        )}

        {step === "verify" && (
          <form onSubmit={submitVerify} className="space-y-4" data-testid="mfa-verify">
            <h1 className="text-center text-xl font-semibold text-ink-heading">Two-factor authentication</h1>
            <p className="text-center text-sm text-ink-muted">Enter the 6-digit code from your authenticator app.</p>
            <input data-testid="mfa-verify-code" inputMode="numeric" placeholder="123456" className={`${inputCls} text-center tracking-widest`} value={code} onChange={(e) => setCode(e.target.value)} />
            <div className="text-center text-xs text-ink-muted">or use a backup code</div>
            <input data-testid="mfa-backup-code" placeholder="XXXX-XXXX" className={`${inputCls} text-center`} value={backupInput} onChange={(e) => setBackupInput(e.target.value)} />
            <Btn data-testid="mfa-verify-submit" type="submit" variant="teal" disabled={busy} className="w-full">{busy ? "Verifying…" : "Verify & sign in"}</Btn>
          </form>
        )}

        {step === "backup" && (
          <div className="space-y-4" data-testid="mfa-backup">
            <h1 className="text-center text-xl font-semibold text-ink-heading">Save your recovery codes</h1>
            <p className="text-center text-sm text-ink-muted">Store these safely. Each can be used once if you lose your authenticator.</p>
            <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-100 p-4 font-mono text-sm text-ink-body">
              {backupCodes.map((c) => (<div key={c} className="text-center">{c}</div>))}
            </div>
            <Btn data-testid="backup-continue" onClick={finishBackup} className="w-full">I've saved them — continue</Btn>
          </div>
        )}
      </div>
    </div>
  );
}
